import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import logo from '../../assets/img.png';
import platforms from '../../assets/img_1.png';
import { getAccounts } from '../../api/client';
import {
  appSearchFromSearch,
  channelsPath,
  environmentPath,
} from '../../app/routes';
import { AsyncImage } from '../../components/AsyncImage';
import { Button } from '../../components/Button';
import { ErrorState } from '../../components/ErrorState';
import { Spinner } from '../../components/Spinner';
import { focusGroupClassName, focusManager } from '../../helpers/spatialFocus';
import { unloadSdks } from '../../lib/sdk';
import type { Account } from '../../types/app';

const groupAccounts = (accounts: Account[]): Record<string, Account[]> =>
  accounts.reduce<Record<string, Account[]>>((groups, account) => {
    const group = groups[account.environment] || [];
    if (!group.some((item) => item.accountName === account.accountName)) {
      group.push(account);
    }
    groups[account.environment] = group;
    return groups;
  }, {});

interface SelectionRowProps<T> {
  activeValue?: string;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  items: T[];
  onSelect: (item: T) => void;
  title: string;
}

const SelectionRow = <T,>({
  activeValue,
  getKey,
  getLabel,
  items,
  onSelect,
  title,
}: SelectionRowProps<T>) => (
  <section className={focusGroupClassName}>
    <h2 className="px-4 text-2xl font-medium sm:px-6">{title}</h2>
    <div className="mt-3 flex overflow-x-auto px-4 pb-7 pt-2 sm:px-6">
      {items.map((item, index) => {
        const key = getKey(item);
        return (
          <Button
            key={key}
            active={key === activeValue}
            className={index === 0 ? '' : 'ml-4'}
            data-focus-key={`${title}:${key}`}
            onClick={() => onSelect(item)}
          >
            {getLabel(item)}
          </Button>
        );
      })}
    </div>
  </section>
);

export const AccountsPage = () => {
  const { env: routeEnvironment = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const groupedAccounts = useMemo(
    () => (accounts ? groupAccounts(accounts) : {}),
    [accounts],
  );
  const environments = useMemo(
    () => Object.keys(groupedAccounts).reverse(),
    [groupedAccounts],
  );
  const selectedEnvironment = groupedAccounts[routeEnvironment]
    ? routeEnvironment
    : environments[0] || '';

  useEffect(() => {
    unloadSdks();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAccounts()
      .then((items) => {
        if (cancelled) return;
        setAccounts(items);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(
            reason instanceof Error
              ? reason.message
              : 'Unable to load accounts',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (!accounts) return;
    const frame = window.requestAnimationFrame(() =>
      focusManager.ensureFocus(
        document.getElementById('accounts-page') || undefined,
      ),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [accounts, selectedEnvironment]);

  if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={() => {
          setAccounts(null);
          setError(null);
          setAttempt((value) => value + 1);
        }}
      />
    );
  }

  return (
    <main
      id="accounts-page"
      className="min-h-full w-full overflow-y-auto px-4 py-8 sm:px-10 sm:py-10 lg:px-20 lg:py-14"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-center">
        <AsyncImage
          src={logo}
          alt="In The Game"
          className="h-32 w-full max-w-3xl sm:h-48"
        />
        <AsyncImage
          src={platforms}
          alt="Supported platforms"
          className="mt-6 hidden h-28 w-4/5 max-w-4xl sm:block"
        />
      </div>

      {!accounts ? (
        <div className="flex min-h-[260px] items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="mx-auto mt-8 max-w-[1600px]">
          <SelectionRow
            title="Select environment"
            activeValue={selectedEnvironment}
            items={environments}
            getKey={(environment) => environment}
            getLabel={(environment) => environment}
            onSelect={(environment) =>
              navigate(
                `${environmentPath(environment)}${appSearchFromSearch(location.search)}`,
                { replace: true },
              )
            }
          />

          {selectedEnvironment && groupedAccounts[selectedEnvironment] && (
            <SelectionRow
              title="Select account"
              items={groupedAccounts[selectedEnvironment]}
              getKey={(account) => account.accountId}
              getLabel={(account) => account.accountName}
              onSelect={(account) =>
                navigate(
                  `${channelsPath(account.environment, account.accountId)}${appSearchFromSearch(location.search)}`,
                )
              }
            />
          )}
        </div>
      )}
    </main>
  );
};
